import { useMemo } from 'react';

import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';

import {
  Configuration,
  ConfigurationsApi,
  CreateConfigurationConfigurationsPostRequest,
  DeleteConfigurationConfigurationsIdDeleteRequest,
  DisableConfigurationConfigurationsIdSetStatusPutRequest,
  GroundingsApi,
  PageConfigurationSchema,
  ReadGroundingsGroundingsGetRequest,
  UpdateConfigurationConfigurationsIdPutRequest,
} from '@ais-ui/api-spec/grounding';
import { getBasePath } from './getAISBasePath';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const useGroundingRequestConfiguration = () => {
  const credentials = useCredentials();
  const basePath = getBasePath();
  const username = credentials?.username || '';
  const password = credentials?.password || '';

  const authHeader = getAuthHeaderFromCredentials(username, password);
  return new Configuration({
    basePath: `${basePath}api/ingress/grounding`,
    headers: {
      'X-Application-Name': 'marinepulse',
      Authorization: authHeader,
    },
  });
};

export const useGroundingApi = () => {
  const configuration = useGroundingRequestConfiguration();
  const apiClientWrapper = useApiClientWrapper();
  const groundingApi = useMemo(
    () => new GroundingsApi(configuration),
    [configuration]
  );
  return apiClientWrapper(groundingApi) as GroundingsApi;
};

export const useGroundingConfigurationApi = () => {
  const configuration = useGroundingRequestConfiguration();
  const groundingApi = useMemo(
    () => new ConfigurationsApi(configuration),
    [configuration]
  );
  return groundingApi;
};

export const useGetGroundingsWithDateTimes = (
  requestParams: ReadGroundingsGroundingsGetRequest,
  enabled = true
) => {
  const groundingApi = useGroundingApi();

  return useQuery(
    ['groundings', requestParams],
    async () => {
      const response = await groundingApi.readGroundingsGroundingsGet(
        requestParams
      );

      convertFieldsToDates(response, [
        'items.createdAt',
        'items.modifiedAt',
        'items.groundingTime',
        'items.releaseTime',
      ]);

      return response;
    },
    {
      enabled,
    }
  );
};

export const usePostGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    (requestBody: CreateConfigurationConfigurationsPostRequest) => {
      return groundingConfigurationsApi.createConfigurationConfigurationsPost(
        requestBody
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['groundingAllDefinitions']);
      },
    }
  );
};

export const useFetchAllGroundingConfigurationDefinitions = (
  enabled = true
): UseQueryResult<PageConfigurationSchema> => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();

  return useQuery(
    ['groundingAllDefinitions'],
    async () => {
      return await groundingConfigurationsApi.readConfigurationsConfigurationsGet();
    },
    {
      enabled,
    }
  );
};

export const useDisableGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    (requestBody: DisableConfigurationConfigurationsIdSetStatusPutRequest) => {
      return groundingConfigurationsApi.disableConfigurationConfigurationsIdSetStatusPut(
        requestBody
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['groundingAllDefinitions']);
      },
    }
  );
};

export const useDeleteGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    (requestBody: DeleteConfigurationConfigurationsIdDeleteRequest) => {
      return groundingConfigurationsApi.deleteConfigurationConfigurationsIdDelete(
        requestBody
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['groundingAllDefinitions']);
      },
    }
  );
};

export const useEditGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    (requestBody: UpdateConfigurationConfigurationsIdPutRequest) => {
      return groundingConfigurationsApi.updateConfigurationConfigurationsIdPut(
        requestBody
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['groundingAllDefinitions']);
      },
    }
  );
};
